import { useState, useCallback, useEffect } from 'react';
import { googlePlacesService, PlaceDetails } from '../services/GooglePlacesService';

interface UseGooglePlacesReturn {
  predictions: google.maps.places.AutocompletePrediction[];
  isLoading: boolean;
  error: string | null;
  searchAddress: (input: string) => Promise<void>;
  getAddressDetails: (placeId: string) => Promise<PlaceDetails>;
}

export const useGooglePlaces = (debounceMs = 300): UseGooglePlacesReturn => {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  const searchAddress = useCallback(async (input: string) => {
    // Clear previous timeout if it exists
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (!input.trim()) {
      setPredictions([]);
      return;
    }

    // Set new timeout for debouncing
    const timeout = setTimeout(async () => {
      try {
        setIsLoading(true);
        setError(null);

        const results = await googlePlacesService.getPlacePredictions(input);
        setPredictions(results);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error searching address:', err);
      } finally {
        setIsLoading(false);
      }
    }, debounceMs);

    setSearchTimeout(timeout);
  }, [debounceMs, searchTimeout]);

  const getAddressDetails = useCallback(async (placeId: string): Promise<PlaceDetails> => {
    try {
      setIsLoading(true);
      setError(null);

      const details = await googlePlacesService.getPlaceDetails(placeId);
      return details;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error getting address details:', err);
      return { street: '', number: '' };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    predictions,
    isLoading,
    error,
    searchAddress,
    getAddressDetails
  };
};
